import { createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import type { DocumentType, Language } from '@worldathletics/types';
import type { DocumentEntity } from './documentDataSlice';

export type UrlModalProps = {
  selectedText: string;
  onConfirm: ((url: string) => void) | null;
};

export type DeleteBlockModalData = {
  bookId: string;
  nodeId: string;
  blockId: string;
  blockType: string;
};

export type AddBlockIntent =
  | { mode: 'child'; containerId: string }
  | { mode: 'sibling-before'; targetId: string }
  | { mode: 'sibling-after'; targetId: string };

export type SiteProps = {
  activeBlock?: DocumentEntity;
  addBlockIntent: AddBlockIntent | null;
  selectedAddBlockId: string;
  selectedAddBlockType: DocumentType;
  addNewBlockModal: boolean;
  addNewLangModal: boolean;
  addNewVersionModal: boolean;
  addVersionPrefillLanguage: Language | null;
  editingAction: boolean;
  specialUrlModal: boolean;
  specialUrlModalData: UrlModalProps;
  deleteBlockConfirmationModal: boolean;
  deleteBlockModalData: DeleteBlockModalData | null;
};

const initialState: SiteProps = {
  activeBlock: undefined,
  addBlockIntent: null,
  selectedAddBlockId: '',
  selectedAddBlockType: 'text',
  addNewBlockModal: false,
  addNewLangModal: false,
  addNewVersionModal: false,
  addVersionPrefillLanguage: null,
  editingAction: false,
  specialUrlModal: false,
  specialUrlModalData: {
    selectedText: '',
    onConfirm: null,
  },
  deleteBlockConfirmationModal: false,
  deleteBlockModalData: null,
};

export const siteSlice = createSlice({
  name: 'site',
  initialState,
  reducers: {
    setActiveBlock: (state, action: PayloadAction<DocumentEntity>) => {
      state.activeBlock = action.payload;
    },
    setAddBlockIntent: (
      state,
      action: PayloadAction<AddBlockIntent | null>,
    ) => {
      state.addBlockIntent = action.payload;
    },
    setSelectedBlockId: (
      state,
      action: PayloadAction<{ id: string; type: DocumentType }>,
    ) => {
      state.selectedAddBlockId = action.payload.id;
      state.selectedAddBlockType = action.payload.type;
    },
    setNewBlockModal: (state, action: PayloadAction<boolean>) => {
      state.addNewBlockModal = action.payload;
    },
    setAddLangModal: (state, action: PayloadAction<boolean>) => {
      state.addNewLangModal = action.payload;
    },
    setAddVersionModal: (state, action: PayloadAction<boolean>) => {
      state.addNewVersionModal = action.payload;
      if (!action.payload) {
        state.addVersionPrefillLanguage = null;
      }
    },
    switchFromAddLanguageToAddVersion: (
      state,
      action: PayloadAction<Language | undefined>,
    ) => {
      state.addNewLangModal = false;
      state.addNewVersionModal = true;
      state.addVersionPrefillLanguage = action.payload ?? null;
    },
    clearAddVersionPrefillLanguage: state => {
      state.addVersionPrefillLanguage = null;
    },
    setEditingAction: (state, action: PayloadAction<boolean>) => {
      state.editingAction = action.payload;
    },
    setSpecialUrlModal: (state, action: PayloadAction<UrlModalProps>) => {
      state.specialUrlModalData = action.payload;
    },
    openSpecialUrlModal: (state, action: PayloadAction<boolean>) => {
      state.specialUrlModal = action.payload;
    },
    setDeleteBlockConfirmationModal: (
      state,
      action: PayloadAction<{
        open: boolean;
        data?: DeleteBlockModalData;
      }>,
    ) => {
      state.deleteBlockConfirmationModal = action.payload.open;
      if (action.payload.data) {
        state.deleteBlockModalData = action.payload.data;
      }
    },
  },
});

export const {
  setActiveBlock,
  setAddBlockIntent,
  setSelectedBlockId,
  setNewBlockModal,
  setAddLangModal,
  setAddVersionModal,
  switchFromAddLanguageToAddVersion,
  clearAddVersionPrefillLanguage,
  setEditingAction,
  openSpecialUrlModal,
  setSpecialUrlModal,
  setDeleteBlockConfirmationModal,
} = siteSlice.actions;

export default siteSlice.reducer;
